'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import en from '@/lib/i18n/translations/en.json';
import { 
  Locale, 
  translations, 
  detectLocale, 
  getTranslation,
  supportedLocales,
  localeNames 
} from '@/lib/i18n';

type TranslationKeys = typeof translations.en;

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  supportedLocales: Locale[];
  localeNames: Record<Locale, string>;
}

const I18nContext = createContext<I18nContextType | null>(null);

// Used when a component renders OUTSIDE `I18nProvider` (isolated mounts, unit
// tests, anything that escapes the tree). Resolve the real English string from
// en.json rather than inventing one from the key: a de-camelCased key renders
// as "Robutler Mcp" / "Authorized Oauth Apps", which is worse than untranslated.
// The humanised key stays as the last resort for a key that is not in en.json.
const fallbackT = (key: string, params?: Record<string, string | number>) => {
  const resolved = getTranslation(en, key, params);
  if (resolved !== key) return resolved;
  const last = key.split('.').pop() ?? key;
  return last.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
};

const fallbackContext: I18nContextType = {
  locale: 'en' as Locale,
  setLocale: () => {},
  t: fallbackT,
  supportedLocales,
  localeNames,
};

export function useTranslation() {
  return useContext(I18nContext) ?? fallbackContext;
}

export function useT() {
  const { t } = useTranslation();
  return t;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Detect locale on mount
    const detected = detectLocale();
    setLocaleState(detected);
    setMounted(true);
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    // Update html lang attribute
    document.documentElement.lang = newLocale;
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>) => {
    return getTranslation(translations[locale], key, params);
  }, [locale]);

  // Update html lang on locale change
  useEffect(() => {
    if (mounted) {
      document.documentElement.lang = locale;
    }
  }, [locale, mounted]);

  return (
    <I18nContext.Provider value={{ 
      locale, 
      setLocale, 
      t,
      supportedLocales,
      localeNames
    }}>
      {children}
    </I18nContext.Provider>
  );
}
